<template>
  <v-card class="rsl-fixed-height">
    <v-card-title>
      <v-text-field
        v-model="search"
        append-icon="mdi-magnify"
        :label="$t('REPORTS.SEARCH_TEXT')"
        single-line
        hide-details
      ></v-text-field>
      <v-spacer></v-spacer>
      <v-btn
        v-if="currentRole != 'userRole'"
        small
        class="mr-2"
        color="error"
        @click="promoteReports()"
        :disabled="allHqConns.length == 0"
      >{{ $t('REPORTS.BTNS.PROMOTE') }}</v-btn>
      <v-btn
        v-if="currentRole != 'userRole'"
        small
        class="mr-2"
        color="success"
        @click="buildReport()"
      >{{ $t('REPORTS.BTNS.NEW') }}</v-btn>
    </v-card-title>
    <v-data-table
      :headers="headers"
      :items="reportsList"
      :items-per-page="25"
      :footer-props="{'items-per-page-options':[25, 50, 100, -1]}"
      :search="search"
      class="elevation-1 mx-3 mb-3"
      item-key="ID"
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      loading-text="Loading... Please wait"
      :loading="loading"
    >
      <template slot="item.actions" slot-scope="row">
        <v-btn
          v-if="row.item.IS_LOCKED == 0"
          small
          class="mr-2"
          color="success"
          @click="viewReport(row.item)"
        >
          {{ $t('GENERAL.BTNS.VIEW') }}
        </v-btn>
        <v-btn
          v-if="row.item.IS_LOCKED == 1"
          small
          class="mr-2"
          color="warning"
          @click="showLocked(row.item.LOCKED_BY)"
        >
          <v-icon small>mdi-lock</v-icon>
        </v-btn>
        <v-btn
          v-if="(isAdmin || currentUser == row.item.CREATED_BY) && row.item.IS_LOCKED == 0"
          small
          class="mr-2"
          color="warning"
          @click="editReport(row.item)"
        >
          {{ $t('GENERAL.BTNS.EDIT') }}
        </v-btn>
        <v-btn
          v-if="isAdmin || currentUser == row.item.CREATED_BY"
          small
          class="mr-2"
          color="secondary"
          @click="copyReport(row.item)"
        >
          {{ $t('GENERAL.BTNS.COPY') }}
        </v-btn>
        <v-btn
          v-if="(isAdmin || currentUser == row.item.CREATED_BY) && row.item.IS_LOCKED == 0"
          small
          class="mr-2"
          color="error"
          @click="deleteReport(row.item)"
        >
          {{ $t('GENERAL.BTNS.DELETE') }}
        </v-btn>
      </template>
    </v-data-table>

    <v-dialog
        transition="dialog-bottom-transition"
        max-width="400"
        v-model="deleteDialog"
        :retain-focus="false"
      >
      <template>
        <v-card>
          <v-toolbar
            color="#f87723"
            dark
          >{{ deleteTitle }}</v-toolbar>
          <v-card-text>
            <div class="pt-5">{{ deleteText }}</div>
          </v-card-text>
          <v-card-actions>
            <v-btn
              text
              @click="deleteDialog = false"
            >{{ $t('GENERAL.BTNS.CANCEL') }}</v-btn>
            <v-spacer></v-spacer>
            <v-btn
              text
              @click="confirmDelete({reportId})"
            >{{ $t('GENERAL.BTNS.CONFIRM') }}</v-btn>
          </v-card-actions>
        </v-card>
      </template>
    </v-dialog>

    <v-dialog
      transition="dialog-bottom-transition"
      max-width="900"
      v-model="promoteReportsDialog"
      :retain-focus="false"
      persistent
    >
      <template>
        <v-card>
          <v-toolbar
            color="#f87723"
            dark
          >{{ promoteReportsTitle }}</v-toolbar>
          <v-card-text>
            <div class="pt-5">{{ promoteReportsText }}</div>
            <v-data-table
              v-model="reportPromote"
              :headers="promoteHeaders"
              :items="allReports"
              :items-per-page="10"
              class="mx-3 mb-3"
              item-key="ID"
              sort-by="ID"
              :sort-desc="true"
              show-select
              loading-text="Loading... Please wait"
              :loading="loading"
            >
            </v-data-table>
            <v-form v-model="isFormValid" ref="promoteReport">
              <v-select
                v-model="selectedExternalEngine"
                :label="$t('REPORTS.PROMOTE.FIELDS.ENGINE')"
                :items="allHqConns"
                item-text="ENGINE_NAME"
                item-value="URL"
                :rules="[rules.required]"
              ></v-select>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-btn
              text
              @click="cancelPromote()"
            >{{ $t('GENERAL.BTNS.CANCEL') }}</v-btn>
            <v-spacer></v-spacer>
            <v-btn
              text
              :disabled="!isFormValid"
              @click="confirmPromoteReports()"
            >{{ $t('GENERAL.BTNS.CONFIRM') }}</v-btn>
          </v-card-actions>
        </v-card>
      </template>
    </v-dialog>

    <v-dialog
      transition="dialog-bottom-transition"
      max-width="900"
      v-model="promoteCheckDialog"
      :retain-focus="false"
      persistent
    >
      <template>
        <v-card>
          <v-toolbar
            color="#f87723"
            dark
          >{{ promoteCheckTitle }}</v-toolbar>
          <v-card-text>
            <div class="pt-5">{{ promoteCheckText }}</div>
            <div class="pt-5" v-html="promoteCheckText2"></div>
          </v-card-text>
          <v-card-actions>
            <v-btn
              small
              class="mr-2"
              color="secondary"
              @click="promoteCheckCancel"
            >{{ $t('GENERAL.BTNS.CLOSE') }}</v-btn>
            <v-spacer></v-spacer>
            <v-btn
              v-if="promoteOverwrite > 0 && promoteCount > 0"
              small
              class="mr-2"
              color="warning"
              @click="promoteCheckConfirm"
            >{{ $t('REPORTS.BTNS.NO_OVERWRITE') }}</v-btn>
            <v-spacer></v-spacer>
            <v-btn
              v-if="promoteOverwrite > 0"
              small
              class="mr-2"
              color="error"
              @click="promoteCheckOverwrite"
            >{{ $t('REPORTS.BTNS.OVERWRITE') }}</v-btn>
            <v-btn
              v-if="promoteOverwrite == 0 && promoteCount > 0"
              small
              class="mr-2"
              color="success"
              @click="promoteCheckConfirm"
            >{{ $t('GENERAL.BTNS.CONFIRM') }}</v-btn>
          </v-card-actions>
        </v-card>
      </template>
    </v-dialog>

    <v-dialog
      transition="dialog-bottom-transition"
      max-width="900"
      v-model="promoteResultsDialog"
      :retain-focus="false"
      persistent
    >
      <template>
        <v-card>
          <v-toolbar
            color="#f87723"
            dark
          >{{ promoteResultsTitle }}</v-toolbar>
          <v-card-text>
            <div class="pt-5" v-html="promoteResultsText"></div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              text
              @click="promoteResultsDialog = false"
            >{{ $t('GENERAL.BTNS.CLOSE') }}</v-btn>
          </v-card-actions>
        </v-card>
      </template>
    </v-dialog>

    <v-dialog
        transition="dialog-bottom-transition"
        max-width="400"
        v-model="showLockedDialog"
        :retain-focus="false"
        persistent
      >
      <template>
        <v-card>
          <v-toolbar
            color="#f87723"
            dark
          >{{ lockedTitle }}</v-toolbar>
          <v-card-text>
            <div class="pt-5">{{ lockedText }}</div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              text
              @click="showLockedDialog = false"
            >{{ $t('GENERAL.BTNS.CLOSE') }}</v-btn>
          </v-card-actions>
        </v-card>
      </template>
    </v-dialog>

    <v-snackbar
      v-model="snack"
      :timeout="3000"
      :color="snackColor"
    >
      {{ snackText }}

      <template v-slot:action="{ attrs }">
        <v-btn
          v-bind="attrs"
          text
          @click="snack = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>

  </v-card>
</template>

<script>
  import $ from 'jquery';
  import { mapGetters } from 'vuex';
  export default {
    name: 'formbuilder',
    computed: {
      ...mapGetters(['allReports', 'reportLocked', 'reportLockedUser', 'allHqConns', 'promoteReportData', 'promoteReportRes', 'userGroups'])
    },
    data: function() {
      return {
        currentUser: '',
        currentRole: this.$store.state.currentRole,
        isAdmin: false,
        loading: false,
        isFormValid: false,
        rules: {
          required: value => !!value || 'Required'
        },
        deleteDialog: false,
        deleteTitle: '',
        deleteText: '',
        showLockedDialog: false,
        lockedTitle: '',
        lockedText: '',
        theReportTitle: '',
        reportId: '',
        reportsList: [],
        search: '',
        promoteReportsDialog: false,
        promoteReportsTitle: '',
        promoteReportsText: '',
        promoteCheckDialog: false,
        promoteCheckTitle: '',
        promoteCheckText: '',
        promoteCheckText2: '',
        promoteCount: 0,
        promoteOverwrite: 0,
        promoteResultsDialog: false,
        promoteResultsTitle: '',
        promoteResultsText: '',
        reportPromote: [],
        selectedExternalEngine: '',
        sortBy: '',
        sortDesc: false,
        headers: [
          {
            text: this.$t('REPORTS.FIELDS.REPORT_ID'),
            align: 'start',
            sortable: true,
            value: 'ID'
          },
          { text: this.$t('REPORTS.FIELDS.REPORT_TITLE'), sortable: true, value: 'REPORT_TITLE' },
          { text: this.$t('REPORTS.FIELDS.REPORT_DESC'), value: 'REPORT_DESC' },
          { text: this.$t('REPORTS.FIELDS.CREATED_ON'), value: 'CREATED_ON', sortable: false },
          { text: this.$t('REPORTS.FIELDS.CREATED_BY'), value: 'CREATED_BY' },
          { text: this.$t('REPORTS.FIELDS.UPDATED_ON'), value: 'UPDATED_ON', sortable: false },
          { text: this.$t('REPORTS.FIELDS.UPDATED_BY'), value: 'UPDATED_BY' },
          { text: this.$t('REPORTS.FIELDS.ACTIONS'), value: 'actions', sortable: false }
        ],
        promoteHeaders: [
          { text: this.$t('REPORTS.FIELDS.REPORT_ID'), align: 'start', value: 'ID' },
          { text: this.$t('REPORTS.FIELDS.REPORT_TITLE'), value: 'REPORT_TITLE' }
        ],
        snack: false,
        snackColor: '',
        snackText: ''
      }
    },
    created() {
      this.unsubscribe = this.$store.subscribe((mutation) => {

        if(mutation.type === 'PROMOTE_REPORT_DATA') {
          this.promoteCount = 0;
          this.promoteOverwrite = 0;
          let html = '';
          let reportExists = false;
          
          for(let i = 0; i < this.reportPromote.length; i++) {
            reportExists = false;
            for(let j = 0; j < this.promoteReportData.length; j++) {
              if(this.reportPromote[i].REPORT_TITLE == this.promoteReportData[j].REPORT_TITLE) {
                reportExists = true;
                if(this.promoteReportData[j].IS_LOCKED) {
                  html += '<p class="red--text font-weight-bold">' + this.$t('REPORTS.PROMOTE.REPORT') + ' <em>' + this.reportPromote[i].REPORT_TITLE + '</em> ' + this.$t('REPORTS.PROMOTE.LOCKED') + '</p>';
                }else {
                  this.promoteOverwrite++;
                  html += '<p class="orange--text font-weight-bold">' + this.$t('REPORTS.PROMOTE.REPORT') + ' <em>' + this.reportPromote[i].REPORT_TITLE + '</em> ' + this.$t('REPORTS.PROMOTE.EXISTS') + '</p>';
                }
              }
            }

            if(!reportExists) {
              this.promoteCount++;
              html += '<p class="green--text font-weight-bold">' + this.$t('REPORTS.PROMOTE.REPORT') + ' <em>' + this.reportPromote[i].REPORT_TITLE + '</em> ' + this.$t('REPORTS.PROMOTE.CREATED') + '</p>';
            }
          }

          if(this.reportPromote.length == 0) {
            html = '<p>' + this.$t('REPORTS.PROMOTE.NONE') + '</p>';
          }

          this.promoteCheckTitle = this.$t('REPORTS.PROMOTE.CHECK_TITLE');
          this.promoteCheckText = this.$t('REPORTS.PROMOTE.CHECK_DESC');
          this.promoteCheckText2 = html;
          this.promoteCheckDialog = true;
        }

        if(mutation.type === 'PROMOTE_REPORT_RES') {
          let html = '';
          let reportNames = [];

          for(let i = 0; i < this.promoteReportRes.length; i++) {
            const reportKeys = Object.keys(this.promoteReportRes[i]);
    
            reportKeys.forEach((key) => {
              if(this.promoteReportRes[i][key] == 'insert') {
                html += '<p>' + this.$t('REPORTS.PROMOTE.REPORT') + ' ' + key + ' ' + this.$t('REPORTS.PROMOTE.INSERT_SUCCESS') + '</p>';
              }
              if(this.promoteReportRes[i][key] == 'update') {
                html += '<p>' + this.$t('REPORTS.PROMOTE.REPORT') + ' ' + key + ' ' + this.$t('REPORTS.PROMOTE.UPDATE_SUCCESS') + '</p>';
              }
              reportNames.push(key);
            });
          }

          if(reportNames.length > 0) {
            this.updateLog('Report', 'Promoted', 'Reports ' + reportNames.toString() + ' promoted to ' + this.selectedExternalEngine + '.');
          }

          this.promoteResultsTitle = this.$t('REPORTS.PROMOTE.RESULT_TITLE');
          this.promoteResultsText = html;
          this.promoteResultsDialog = true;
          this.$refs.promoteReport.reset();
          this.reportPromote = [];
        }

        if(mutation.type === 'ALL_REPORTS') {
          this.$store.dispatch('getUserGroups');
        }

        if(mutation.type === 'USER_GROUPS') {
          this.buildList();
        }

      });
      this.$store.dispatch('getAllHqConnections');
      this.refreshReports();
      this.currentUser = this.$store.state.currentUser;
      this.isAdmin = this.$store.state.isAdmin;
    },
    beforeDestroy() {
      this.unsubscribe();
    },
    methods: {
      buildList() {
        let rList = [];
        for(let i = 0; i < this.allReports.length; i++) {
          let match = false;
          let groups = JSON.parse(this.allReports[i].REPORT_GROUPS);
          for(let j = 0; j < groups.length; j++) {
            if(!match) {
              for(let k = 0; k < this.userGroups.length; k++) {
                if(groups[j] == this.userGroups[k]) {
                  rList.push(this.allReports[i]);
                  match = true;
                  break;
                }
              }
            }
          }
        }
        this.reportsList = rList;
      },
      refreshReports() {
        this.loading = true;
        this.$store.dispatch('getAllReports').then( () => {
          this.loading = false;
          // Apply sort field and order
          let field = this.$store.state.reportSortField;
          let sort = this.$store.state.reportSortOrder;
          if(field == null) {
            this.sortBy = 'ID';
            this.sortDesc = true;
          }else if (sort == 'none') {
            this.sortBy = '';
            this.sortDesc = false;
          }else {
            this.sortBy = field;

            if(sort == 'descending') {
              this.sortDesc = true;
            }else {
              this.sortDesc = false;
            }
          }
          // Remove previous click events
          $('.text-start.sortable').off();
          // Add new click events for storing order
          let ref = this;
          $('.text-start.sortable').click(function() {
            let column = $(this)[0].firstChild.innerHTML;
            let order = $(this).attr('aria-sort');
            
            for(let i = 0; i < ref.headers.length; i++) {
              if(column == ref.headers[i].text) {
                ref.$store.commit('setReportSortField', ref.headers[i].value);
                ref.$store.commit('setReportSortOrder', order);
              }
            }
          });
        });
      },
      buildReport() {
        this.$store.commit('REPORT_TITLE', 'My New Report');
        this.$store.commit('UPDATE_REPORT_ID', 0);
        this.$store.commit('UPDATE_REPORT_ACTION', 'new');
        this.$router.push({ name: 'BuildReport' });
      },
      editReport(item) {
        let payload = {
          'reportId': item.ID,
          'checkType': 'single'
        };
        this.$store.dispatch('reportLockCheck', payload).then(() => {
          if(this.reportLocked) {
            this.showLocked(this.reportLockedUser);
            this.refreshReports();
          }else {
            this.$store.commit('EDITING_REPORT', true);
            this.$store.commit('UPDATE_REPORT_ID', item.ID);
            this.$store.commit('UPDATE_REPORT_ACTION', 'edit');
            this.$router.push({ name: 'BuildReport' });
          }
        });
      },
      copyReport(item) {
        let createdBy = this.$store.state.currentUser;

        let dt = new Date();
        let dateTime = new Intl.DateTimeFormat('en-GB', 
          {
            day: 'numeric',
            month: 'numeric', 
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
            hour12: false
          }).format(dt);

        let createdOn = dateTime;

        let payload = {
          'reportId': item.ID,
          'createdBy': createdBy,
          'createdOn': createdOn
        }
        this.$store.dispatch('copyReport', payload).then( () => {
          this.updateLog('Report', 'Copied', 'Report ' + item.REPORT_TITLE + ' duplicated.');

          this.refreshReports();
        });
      },
      viewReport(item) {
        this.$store.commit('REPORT_TITLE', item.REPORT_TITLE);
        this.$store.commit('REPORT_SQL', item.REPORT_SQL);
        this.$router.push({ name: 'ViewReport' });
      },
      deleteReport(item) {
        let payload = {
          'reportId': item.ID,
          'checkType': 'single'
        };
        this.$store.dispatch('reportLockCheck', payload).then(() => {
          if(this.reportLocked) {
            this.showLocked(this.reportLockedUser);
            this.refreshReports();
          }else {
            this.deleteTitle = this.$t('REPORTS.DELETE_REPORT.TITLE');
            this.deleteText = this.$t('REPORTS.DELETE_REPORT.TITLE') + ' ' + item.REPORT_TITLE;
            this.theReportTitle = item.REPORT_TITLE;
            this.reportId = item.ID;
            this.deleteDialog = true;
          }
        });
      },
      confirmDelete(item) {
        this.deleteDialog = false;
        let payload = {
          'reportId': item.reportId
        }
        this.$store.dispatch('deleteReport', payload).then( () => {
          this.updateLog('Report', 'Deleted', 'Report ' + this.theReportTitle + ' deleted.');

          this.refreshReports();
          this.snackDelete();
        });
      },
      cancelPromote() {
        this.promoteReportsDialog = false;
        this.$refs.promoteReport.reset();
        this.reportPromote = [];
      },
      promoteReports() {
        this.promoteReportsTitle = this.$t('REPORTS.PROMOTE.TITLE');
        this.promoteReportsText = this.$t('REPORTS.PROMOTE.DESC');
        this.promoteReportsDialog = true;
      },
      confirmPromoteReports() {
        this.promoteReportsDialog = false;
        let promotePayload = [];
        let reportNames = [];

        for(let i = 0; i < this.reportPromote.length; i++) {
          promotePayload.push({ reportName: this.reportPromote[i].REPORT_TITLE, reportId: this.reportPromote[i].ID });
          reportNames.push(this.reportPromote[i].REPORT_TITLE);
        }
        
        let payload = {
          promotes: JSON.stringify(promotePayload),
          engine: this.selectedExternalEngine
        };
        
        this.$store.dispatch('promoteReportsCheck', payload);
      },
      promoteCheckCancel() {
        this.promoteCheckDialog = false;
        this.$refs.promoteReport.reset();
        this.reportPromote = [];
      },
      promoteCheckConfirm() {
        this.promoteCheckDialog = false;
        
        for(let i = 0; i < this.reportPromote.length; i++) {
          for(let j = 0; j < this.promoteReportData.length; j++) {
            if(this.reportPromote[i].REPORT_TITLE == this.promoteReportData[j].REPORT_TITLE) {
              this.reportPromote.splice(i, 1);
            }
          }
        }
        
        let payload = {
          promotes: JSON.stringify(this.reportPromote),
          engine: this.selectedExternalEngine
        };
        
        this.$store.dispatch('promoteReports', payload);
      },
      promoteCheckOverwrite() {
        this.promoteCheckDialog = false;
        
        for(let i = 0; i < this.reportPromote.length; i++) {
          for(let j = 0; j < this.promoteReportData.length; j++) {
            if(this.reportPromote[i].REPORT_TITLE == this.promoteReportData[j].REPORT_TITLE) {
              if(this.promoteReportData[j].IS_LOCKED) {
                this.reportPromote.splice(i, 1);
              }
            }
          }
        }

        let payload = {
          promotes: JSON.stringify(this.reportPromote),
          engine: this.selectedExternalEngine
        };
        
        this.$store.dispatch('promoteReports', payload);
      },
      showLocked(user) {
        this.lockedTitle = this.$t('REPORTS.LOCKED.TITLE');
        this.lockedText = this.$t('REPORTS.LOCKED.DESC') + ' ' + user + '.';
        this.showLockedDialog = true;
      },
      snackDelete () {
        this.snack = true
        this.snackColor = 'success'
        this.snackText = 'Report deleted successfully.'
      },
      updateLog(cat, op, msg) {
        let dt = new Date();
        let dateTime = new Intl.DateTimeFormat('en-GB', 
          {
            day: 'numeric',
            month: 'numeric', 
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
            hour12: false
          }).format(dt);

        let logData = {
          'category': cat,
          'userId': this.$store.state.currentUser,
          'caseId': 0,
          'processName': null,
          'taskName': null,
          'operation': op,
          'msg': msg,
          'dateTime': dateTime,
        };
        this.$store.dispatch('updateOpLog', logData);
      }
    }
  }
</script>
